import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from '../base/utils';

const DEFAULT_MASONRY_GAP_PX = 12;
const DEFAULT_MASONRY_ROW_HEIGHT_PX = 8;
const DEFAULT_MASONRY_COLUMN_WIDTH_PX = 280;
const DEFAULT_MASONRY_ITEM_HEIGHT_PX = 240;
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export interface WorkbenchMasonryItemSize {
  width?: number | string;
  height?: number | string;
  columnSpan?: number;
  rowSpan?: number;
}

export interface WorkbenchMasonryLayoutContext {
  columnCount: number;
  columnSpan?: number;
  columnWidth: number;
  containerWidth: number;
  gap: number;
  itemWidth?: number;
  placementStrategy: 'shelf' | 'shortest' | 'dense';
  rowSpan?: number;
  rowHeight: number;
  size?: WorkbenchMasonryItemSize;
}

export interface WorkbenchMasonryLayoutProps<Item = any> {
  items?: readonly Item[];
  renderItem: (item: Item, context: WorkbenchMasonryLayoutContext) => ReactNode;
  getItemKey?: (item: Item, index: number) => string | number;
  getItemSize?: (item: Item, context: WorkbenchMasonryLayoutContext) => WorkbenchMasonryItemSize | undefined;
  columnWidth?: number | string;
  columnCount?: number;
  gap?: number;
  rowHeight?: number;
  placementStrategy?: 'shelf' | 'shortest' | 'dense';
  containerProps?: Omit<HTMLAttributes<HTMLDivElement>, 'children'>;
  getItemProps?: (
    item: Item,
    context: WorkbenchMasonryLayoutContext,
  ) => Omit<HTMLAttributes<HTMLDivElement>, 'children'>;
  className?: string;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getRootFontSize(node: Element | null) {
  if (typeof window === 'undefined') return 16;
  const target = node || document.documentElement;
  const parsed = Number.parseFloat(window.getComputedStyle(target).fontSize);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
}

function resolveLengthToPixels(value: number | string | undefined, fallback: number, node: Element | null) {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value !== 'string') return fallback;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return fallback;

  if (/^\d+(\.\d+)?$/.test(normalized)) {
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  if (normalized.endsWith('px')) {
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  if (normalized.endsWith('rem')) {
    const parsed = Number.parseFloat(normalized);
    if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
    return parsed * getRootFontSize(node);
  }

  return fallback;
}

function areMeasurementMapsEqual(prev: Record<string, number>, next: Record<string, number>) {
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(next);
  if (prevKeys.length !== nextKeys.length) return false;

  for (const key of nextKeys) {
    if (prev[key] !== next[key]) return false;
  }

  return true;
}

function getMasonryColumnCount({
  containerWidth,
  itemCount,
  targetColumnWidth,
  gap,
}: {
  containerWidth: number;
  itemCount: number;
  targetColumnWidth: number;
  gap: number;
}) {
  if (!containerWidth) return 1;

  const safeItemCount = Math.max(itemCount, 1);
  const maxCandidate = Math.max(
    1,
    Math.min(
      safeItemCount,
      Math.ceil((containerWidth + gap) / (Math.max(targetColumnWidth * 0.58, 1) + gap)),
    ),
  );

  let bestCount = 1;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let candidateCount = 1; candidateCount <= maxCandidate; candidateCount += 1) {
    const actualColumnWidth = (containerWidth - gap * Math.max(candidateCount - 1, 0)) / candidateCount;
    if (!Number.isFinite(actualColumnWidth) || actualColumnWidth <= 0) continue;

    const underflowPenalty = Math.max(0, targetColumnWidth * 0.78 - actualColumnWidth);
    const overflowPenalty = Math.max(0, actualColumnWidth - targetColumnWidth * 1.35);
    const candidateScore = Math.abs(actualColumnWidth - targetColumnWidth) + underflowPenalty * 3 + overflowPenalty * 2;

    if (candidateScore < bestScore) {
      bestScore = candidateScore;
      bestCount = candidateCount;
    }
  }

  return bestCount;
}

function getItemColumnSpan({
  size,
  columnWidth,
  columnCount,
  gap,
}: {
  size?: WorkbenchMasonryItemSize;
  columnWidth: number;
  columnCount: number;
  gap: number;
}) {
  if (Number.isFinite(size?.columnSpan) && Number(size?.columnSpan) > 0) {
    return clamp(Math.round(Number(size?.columnSpan)), 1, columnCount);
  }

  const itemWidth = resolveLengthToPixels(size?.width, columnWidth, null);
  return clamp(Math.ceil((itemWidth + gap) / (columnWidth + gap)), 1, columnCount);
}

function getItemRowSpan({
  size,
  measuredHeight,
  rowHeight,
  gap,
}: {
  size?: WorkbenchMasonryItemSize;
  measuredHeight?: number;
  rowHeight: number;
  gap: number;
}) {
  if (Number.isFinite(size?.rowSpan) && Number(size?.rowSpan) > 0) {
    return Math.max(1, Math.round(Number(size?.rowSpan)));
  }

  const itemHeight = Number.isFinite(measuredHeight) && Number(measuredHeight) > 0
    ? Number(measuredHeight)
    : resolveLengthToPixels(size?.height, DEFAULT_MASONRY_ITEM_HEIGHT_PX, null);

  return Math.max(1, Math.ceil((itemHeight + gap) / (rowHeight + gap)));
}

function getResolvedItemHeight({
  size,
  measuredHeight,
}: {
  size?: WorkbenchMasonryItemSize;
  measuredHeight?: number;
}) {
  return Number.isFinite(measuredHeight) && Number(measuredHeight) > 0
    ? Number(measuredHeight)
    : resolveLengthToPixels(size?.height, DEFAULT_MASONRY_ITEM_HEIGHT_PX, null);
}

export function WorkbenchMasonryLayout<Item = any>({
  items = [],
  renderItem,
  getItemKey = (item: any, index: number) => item?.id ?? item?.key ?? index,
  getItemSize,
  columnWidth = DEFAULT_MASONRY_COLUMN_WIDTH_PX,
  columnCount: requestedColumnCount,
  gap = DEFAULT_MASONRY_GAP_PX,
  rowHeight = DEFAULT_MASONRY_ROW_HEIGHT_PX,
  placementStrategy = 'shelf',
  containerProps,
  getItemProps,
  className,
}: WorkbenchMasonryLayoutProps<Item>) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemNodesRef = useRef(new Map<string, HTMLDivElement>());
  const itemObserverRef = useRef<ResizeObserver | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [measuredHeights, setMeasuredHeights] = useState<Record<string, number>>({});
  const itemKeys = useMemo(
    () => items.map((item, index) => String(getItemKey(item, index))),
    [getItemKey, items],
  );
  const itemKeysSignature = itemKeys.join('|');

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const nextWidth = Math.round(node.getBoundingClientRect().width);
      setContainerWidth((prevWidth) => (prevWidth === nextWidth ? prevWidth : nextWidth));
    };

    updateWidth();

    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    setMeasuredHeights((prev) => {
      const currentItemKeys = new Set(itemKeys);
      const next = Object.fromEntries(
        Object.entries(prev).filter(([key]) => currentItemKeys.has(key)),
      );

      return areMeasurementMapsEqual(prev, next) ? prev : next;
    });
  }, [itemKeys, itemKeysSignature]);

  const resolvedTargetColumnWidth = resolveLengthToPixels(
    columnWidth,
    DEFAULT_MASONRY_COLUMN_WIDTH_PX,
    containerRef.current,
  );
  const isDensePlacement = placementStrategy === 'dense';
  const normalizedRequestedColumnCount = Number.isFinite(requestedColumnCount) && Number(requestedColumnCount) > 0
    ? clamp(Math.round(Number(requestedColumnCount)), 1, 24)
    : undefined;
  const columnCount = normalizedRequestedColumnCount || getMasonryColumnCount({
      containerWidth,
      itemCount: items.length,
      targetColumnWidth: resolvedTargetColumnWidth,
      gap,
    });
  const resolvedColumnWidth = containerWidth > 0
    ? (containerWidth - gap * Math.max(columnCount - 1, 0)) / columnCount
    : resolvedTargetColumnWidth;

  const layoutItems = useMemo(() => items.map((item, index) => {
    const key = itemKeys[index];
    const size = typeof getItemSize === 'function'
      ? getItemSize(item, {
        columnCount,
        columnWidth: resolvedColumnWidth,
        containerWidth,
        gap,
        placementStrategy,
        rowHeight,
      }) || {}
      : {};

    return {
      key,
      item,
      size,
      columnSpan: getItemColumnSpan({
        size,
        columnWidth: resolvedColumnWidth,
        columnCount,
        gap,
      }),
      rowSpan: getItemRowSpan({
        size,
        measuredHeight: measuredHeights[key],
        rowHeight,
        gap,
      }),
    };
  }), [
    columnCount,
    containerWidth,
    gap,
    getItemSize,
    itemKeys,
    items,
    measuredHeights,
    placementStrategy,
    resolvedColumnWidth,
    rowHeight,
  ]);

  useIsomorphicLayoutEffect(() => {
    const nextMeasurements: Record<string, number> = {};

    itemNodesRef.current.forEach((node, key) => {
      const nextHeight = Math.ceil(node.getBoundingClientRect().height);
      if (Number.isFinite(nextHeight) && nextHeight > 0) {
        nextMeasurements[key] = nextHeight;
      }
    });

    setMeasuredHeights((prev) => {
      if (!Object.keys(nextMeasurements).length) return prev;
      const merged = { ...prev, ...nextMeasurements };
      return areMeasurementMapsEqual(prev, merged) ? prev : merged;
    });
  }, [columnCount, isDensePlacement, itemKeysSignature, resolvedColumnWidth]);

  useEffect(() => {
    if (typeof ResizeObserver !== 'function') return undefined;

    const observer = new ResizeObserver((entries) => {
      setMeasuredHeights((prev) => {
        const next = { ...prev };
        let didChange = false;

        for (const entry of entries) {
          const key = (entry.target as HTMLElement).dataset.masonryKey;
          if (!key) continue;
          const nextHeight = Math.ceil(entry.contentRect.height);
          if (!Number.isFinite(nextHeight) || nextHeight <= 0 || next[key] === nextHeight) continue;
          next[key] = nextHeight;
          didChange = true;
        }

        return didChange ? next : prev;
      });
    });

    itemObserverRef.current = observer;
    itemNodesRef.current.forEach((node) => observer.observe(node));

    return () => {
      observer.disconnect();
      itemObserverRef.current = null;
    };
  }, [isDensePlacement, itemKeysSignature]);

  const registerItemNode = (key: string, node: HTMLDivElement | null) => {
    const currentNodes = itemNodesRef.current;
    const previousNode = currentNodes.get(key);

    if (previousNode && previousNode !== node && itemObserverRef.current) {
      itemObserverRef.current.unobserve(previousNode);
    }

    if (!node) {
      currentNodes.delete(key);
      return;
    }

    node.dataset.masonryKey = key;
    currentNodes.set(key, node);

    if (itemObserverRef.current) {
      itemObserverRef.current.observe(node);
    }
  };

  if (!isDensePlacement) {
    const orderedLayout = (() => {
      const columnBottoms = Array.from({ length: columnCount }, () => 0);
      let nextColumnIndex = 0;
      let maxBottom = 0;

      const positionedItems = layoutItems.map(({ key, item, columnSpan, rowSpan, size }) => {
        const resolvedSpan = clamp(columnSpan, 1, columnCount);
        let startColumn = nextColumnIndex;

        if (placementStrategy === 'shortest') {
          let shortestBottom = Number.POSITIVE_INFINITY;
          for (let candidate = 0; candidate <= columnCount - resolvedSpan; candidate += 1) {
            const candidateBottom = Math.max(...columnBottoms.slice(candidate, candidate + resolvedSpan));
            if (candidateBottom < shortestBottom) {
              shortestBottom = candidateBottom;
              startColumn = candidate;
            }
          }
        } else {
          if (startColumn + resolvedSpan > columnCount) {
            startColumn = 0;
          }

          startColumn = Math.min(startColumn, columnCount - resolvedSpan);
        }

        const itemWidth = resolvedColumnWidth * resolvedSpan + gap * Math.max(resolvedSpan - 1, 0);
        const itemHeight = getResolvedItemHeight({
          size,
          measuredHeight: measuredHeights[key],
        });
        const top = Math.max(...columnBottoms.slice(startColumn, startColumn + resolvedSpan));
        const left = startColumn * (resolvedColumnWidth + gap);
        const bottom = top + itemHeight;

        for (let columnIndex = startColumn; columnIndex < startColumn + resolvedSpan; columnIndex += 1) {
          columnBottoms[columnIndex] = bottom + gap;
        }

        maxBottom = Math.max(maxBottom, bottom);
        if (placementStrategy !== 'shortest') {
          nextColumnIndex = startColumn + resolvedSpan;

          if (nextColumnIndex >= columnCount) {
            nextColumnIndex = 0;
          }
        }

        return {
          key,
          item,
          columnSpan: resolvedSpan,
          itemHeight,
          itemWidth,
          left,
          rowSpan,
          size,
          top,
        };
      });

      return {
        containerHeight: Math.max(0, maxBottom),
        positionedItems,
      };
    })();

    return (
      <div
        ref={containerRef}
        {...containerProps}
        className={cn('relative w-full', className, containerProps?.className)}
        style={{ ...containerProps?.style, height: `${orderedLayout.containerHeight}px` }}
      >
        {orderedLayout.positionedItems.map(({ key, item, columnSpan, rowSpan, size, itemWidth, left, top }) => {
          const unresolvedSingleColumnWidth = containerWidth <= 0 && columnCount === 1;
          const itemWidthValue = unresolvedSingleColumnWidth ? '100%' : `${itemWidth}px`;
          const layoutContext: WorkbenchMasonryLayoutContext = {
            columnCount,
            columnSpan,
            columnWidth: resolvedColumnWidth,
            containerWidth,
            gap,
            itemWidth,
            placementStrategy,
            rowSpan,
            rowHeight,
            size,
          };
          const itemProps = getItemProps?.(item, layoutContext);

          return (
            <div
              key={key}
              {...itemProps}
              className={cn('absolute min-w-0', itemProps?.className)}
              style={{
                ...itemProps?.style,
                left: unresolvedSingleColumnWidth ? 0 : `${left}px`,
                top: `${top}px`,
                width: itemWidthValue,
                maxWidth: itemWidthValue,
              }}
            >
              <div ref={(node) => registerItemNode(key, node)} role="presentation" className="min-w-0">
                {renderItem(item, layoutContext)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      {...containerProps}
      className={cn('grid w-full items-start', className, containerProps?.className)}
      style={{
        ...containerProps?.style,
        gap: `${gap}px`,
        gridAutoFlow: 'row dense',
        gridAutoRows: `${rowHeight}px`,
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
      }}
    >
      {layoutItems.map(({ key, item, columnSpan, rowSpan, size }) => {
        const layoutContext: WorkbenchMasonryLayoutContext = {
              columnCount,
              columnSpan,
              columnWidth: resolvedColumnWidth,
              containerWidth,
              gap,
              placementStrategy,
              rowSpan,
              rowHeight,
              size,
        };
        const itemProps = getItemProps?.(item, layoutContext);
        return (
          <div
            key={key}
            {...itemProps}
            className={cn('min-w-0', itemProps?.className)}
            style={{
              ...itemProps?.style,
              gridColumn: `span ${columnSpan}`,
              gridRow: `span ${rowSpan}`,
            }}
          >
            <div ref={(node) => registerItemNode(key, node)} role="presentation" className="min-w-0">
              {renderItem(item, layoutContext)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
